import { ClassProvider, inject, Injectable, Injector } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { DefaultTitleStrategy, Router, RouterStateSnapshot, TitleStrategy } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AppSettings } from "../config/app-settings.config";
import { NonTranslatable } from "../constants/non-translatable";

export const provideAppTitleStrategy = (): ClassProvider => ({
  provide: TitleStrategy,
  useClass: AppTitleStrategy,
  multi: false,
});

@Injectable()
export class AppTitleStrategy extends DefaultTitleStrategy implements TitleStrategy {
  protected readonly translate = inject(TranslateService);
  protected readonly injector = inject(Injector);

  private readonly defaultTranslationKey: string = "WEB_PAGE_TITLE.DEFAULT";
  private readonly defaultTitle: string = AppSettings.appName;

  private readonly interpolateParams = <const>{ appName: AppSettings.appName, sPrName: NonTranslatable.ServiceProtect };

  constructor() {
    super(inject(Title));
    this.translate.onTranslationRefresh.pipe(takeUntilDestroyed()).subscribe(() => {
      const snapshot: RouterStateSnapshot | null = this.injector.get(Router, null, { optional: true })?.routerState?.snapshot ?? null;
      if (snapshot) this.updateTitle(snapshot);
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const resolvedTitle = this.buildTitle(snapshot);

    if (!resolvedTitle?.trim()) {
      return;
    }

    const translation: unknown = this.translate.instant(resolvedTitle, this.interpolateParams);

    if (translation && typeof translation === "string" && translation.trim() && translation !== resolvedTitle) {
      if (this.title.getTitle() !== translation) {
        this.title.setTitle(translation);
      }
      return;
    }

    const defaultTranslation: unknown = this.defaultTranslationKey
      ? (this.translate.instant(this.defaultTranslationKey, this.interpolateParams) ?? this.defaultTranslationKey)
      : this.defaultTranslationKey;

    if (
      defaultTranslation &&
      typeof defaultTranslation === "string" &&
      defaultTranslation.trim() &&
      defaultTranslation !== this.defaultTranslationKey
    ) {
      if (this.title.getTitle() !== defaultTranslation) {
        this.title.setTitle(defaultTranslation);
      }
      return;
    }

    if (this.title.getTitle() !== this.defaultTitle) {
      this.title.setTitle(this.defaultTitle);
    }
  }
}
